// The session view (PRD 002). Two phases share one screen: a pre-session config
// (mic + checks) and the live run (transcript spine + speaker management + a
// quiet placeholder for the AI panel that PRDs 003–005 will fill). The store-
// bound hooks live in <LiveRun>, which only mounts once a controller — and thus
// a store — exists.

import {
  Mic,
  PanelRight,
  Pause,
  Play,
  Settings as SettingsIcon,
  Square
} from 'lucide-react'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useTopBar } from '@/components/shell/AppShell'
import { AiPresenceChips } from '@/components/shell/AiPresenceChips'
import { ConnectionChip } from '@/components/shell/ConnectionChip'
import { DebugPanel } from '@/components/shell/DebugPanel'
import { FocusModal } from '@/components/shell/FocusModal'
import { LevelMeter } from '@/components/shell/LevelMeter'
import { SpeakerList } from '@/components/shell/SpeakerList'
import { TranscriptView } from '@/components/shell/TranscriptView'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { consumePairedSession, consumePendingTitle } from '@/lib/bridge/autopair'
import { resolveBridgeEndpoint } from '@/lib/bridge/config'
import { useContacts } from '@/lib/contacts/contactStore'
import { loadKeys } from '@/lib/keys/keyStore'
import { PROVIDER_LABELS, type StoredKeys } from '@/lib/keys/types'
import {
  createSessionController,
  type SessionController
} from '@/lib/session/sessionController'
import { loadPersistedSession } from '@/lib/session/sessionPersistence'
import { remoteLoadSession } from '@/lib/session/sessionsRemote'
import {
  useAiDetail,
  useAiStatus,
  useAiTurns,
  useConnection,
  useConnectionDetail,
  useDelivery,
  useError,
  useAiPresence,
  useFinals,
  useInterim,
  useLevel,
  useSessionTitle,
  useSpeakers
} from '@/lib/session/useSession'
import { confirmLeave } from '@/lib/session/leaveGuard'
import { isDebugPanelEnabled, setDebugPanelEnabled } from '@/lib/debug/debugParams'
import { listMics } from '@/lib/transcription/audio/micCapture'
import { newSessionId } from '@/lib/transcription/ids'

// The whiteboard pane mounts the SAME shared canvas as #/board, but Excalidraw is a
// large dependency — lazy-load it so it never enters the #/session entry chunk
// (PRD 010 AC4). It loads only when the user opens the board pane.
const BoardCanvas = lazy(() =>
  import('@/components/board/BoardCanvas').then((m) => ({ default: m.BoardCanvas }))
)

function defaultTitle(): string {
  const now = new Date()
  return `Session · ${now.toLocaleDateString()} ${now.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })}`
}

export function Session({ sessionId }: { sessionId?: string }) {
  const [keys, setKeys] = useState<StoredKeys | null>(null)
  const [controller, setController] = useState<SessionController | null>(null)
  const [restoring, setRestoring] = useState<boolean>(Boolean(sessionId))

  useEffect(() => {
    loadKeys().then(setKeys)
  }, [])

  // Restore a persisted session (from a deep link / reload / sidebar select)
  // into an IDLE controller — capture only re-acquires on an explicit Resume
  // (002 F7). Skipped when the active controller already matches the id.
  useEffect(() => {
    if (!sessionId || !keys) return
    if (controller?.store.getSnapshot().id === sessionId) {
      setRestoring(false)
      return
    }
    let cancelled = false
    setRestoring(true)
    // Server is the system of record (PRD 011): hydrate the snapshot from the
    // backend, falling back to the IndexedDB cache when it's unreachable / absent.
    void (async () => {
      const rec = (await remoteLoadSession(sessionId)) ?? (await loadPersistedSession(sessionId))
      if (cancelled) return
      if (rec) {
        setController(
          createSessionController({
            init: {
              id: rec.id,
              title: rec.title,
              createdAt: rec.createdAt,
              finals: rec.finals,
              speakers: rec.speakers,
              aiTurns: rec.aiTurns
            },
            providerId: keys.provider,
            apiKey: keys.transcriptionKey ?? '',
            region: keys.speechmaticsRegion,
            sonioxDirectKey: keys.sonioxDirectKey,
            bridge: keys.bridgeToken
              ? {
                endpoint: resolveBridgeEndpoint(keys.bridgeUrl),
                token: keys.bridgeToken,
                triggerName: keys.aiTriggerName
              }
              : undefined,
            tts: keys.elevenLabsKey
              ? {
                apiKey: keys.elevenLabsKey,
                voiceId: keys.ttsVoiceId,
                outputDeviceId: keys.ttsOutputDeviceId || undefined
              }
              : undefined
          })
        )
      }
      setRestoring(false)
    })()
    return () => {
      cancelled = true
    }
  }, [sessionId, keys, controller])

  // Release a controller when it's swapped out or the view unmounts.
  useEffect(() => {
    if (!controller) return
    return () => controller.dispose()
  }, [controller])

  if (!keys || restoring) {
    return <Centered>Loading…</Centered>
  }

  if (!controller) {
    return (
      <ConfigPanel
        keys={keys}
        onStart={(c) => {
          setController(c)
          c.start().catch(() => {
            /* errors surface through the store's connection/error state */
          })
          // Carry the id in the URL so a mid-session reload restores it (AC5).
          window.location.hash = `#/session/${encodeURIComponent(
            c.store.getSnapshot().id
          )}`
        }}
      />
    )
  }

  return (
    <LiveRun
      controller={controller}
      // The board pane reuses the session's pairing (same token/userId → that
      // user's BoardDO). Absent a bridge token, the whiteboard toggle is hidden.
      boardEndpoint={resolveBridgeEndpoint(keys.bridgeUrl)}
      boardToken={keys.bridgeToken ?? null}
      onNew={() => {
        // Starting a new session leaves a live round — gate it (no-op when the
        // session isn't live; the guard resolves immediately).
        void confirmLeave().then((ok) => {
          if (!ok) return
          setController(null)
          window.location.hash = '#/session'
        })
      }}
    />
  )
}

// --- config phase -----------------------------------------------------------

function ConfigPanel({
  keys,
  onStart
}: {
  keys: StoredKeys
  onStart: (controller: SessionController) => void
}) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [deviceId, setDeviceId] = useState<string>('')
  // Prefill from the launch URL's ?title= (PRD 007), else a timestamped default.
  const [title, setTitle] = useState(() => consumePendingTitle() || defaultTitle())
  const [expected, setExpected] = useState('')
  const [captureRemote, setCaptureRemote] = useState(false)
  const [micError, setMicError] = useState<string | null>(null)

  const hasKey = Boolean(keys.transcriptionKey)
  const acknowledged = Boolean(keys.disclosureAcceptedAt)
  const ready = hasKey && acknowledged

  async function enableMic() {
    setMicError(null)
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true })
      s.getTracks().forEach((t) => t.stop())
      const mics = await listMics()
      setDevices(mics)
      if (!deviceId && mics[0]) setDeviceId(mics[0].deviceId)
    } catch (err) {
      setMicError(err instanceof Error ? err.message : String(err))
    }
  }

  async function start() {
    // Acquire the remote tab/system audio inside this click — getDisplayMedia
    // needs a user gesture, and handling cancel here (vs. mid-connect) keeps the
    // error in front of the user before the session spins up.
    let tabStream: MediaStream | null = null
    if (captureRemote) {
      setMicError(null)
      try {
        tabStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        })
      } catch {
        setMicError('Remote audio capture was cancelled — press Start to retry.')
        return
      }
      if (tabStream.getAudioTracks().length === 0) {
        tabStream.getTracks().forEach((t) => t.stop())
        setMicError(
          'No tab audio was shared. Pick a browser tab and keep “Share tab audio” checked.'
        )
        return
      }
      // Audio is all we need — drop the video track immediately.
      tabStream.getVideoTracks().forEach((t) => t.stop())
    }

    const maxSpeakers = expected ? Number(expected) : undefined
    const controller = createSessionController({
      // Adopt the paired server session id (from a /brainstorm link) so the SPA
      // snapshot, the durable event log, and the sessions index all key on the
      // SAME id; a manual New session (unpaired) gets a fresh local id (PRD 011).
      init: { id: consumePairedSession() || newSessionId(), title: title.trim() || defaultTitle() },
      providerId: keys.provider,
      apiKey: keys.transcriptionKey ?? '',
      region: keys.speechmaticsRegion,
      deviceId: deviceId || undefined,
      tabStream,
      sonioxDirectKey: keys.sonioxDirectKey,
      bridge: keys.bridgeToken
        ? {
          endpoint: resolveBridgeEndpoint(keys.bridgeUrl),
          token: keys.bridgeToken,
          triggerName: keys.aiTriggerName
        }
        : undefined,
      tts: keys.elevenLabsKey
        ? {
          apiKey: keys.elevenLabsKey,
          voiceId: keys.ttsVoiceId,
          outputDeviceId: keys.ttsOutputDeviceId || undefined
        }
        : undefined,
      maxSpeakers:
        maxSpeakers && Number.isFinite(maxSpeakers) ? maxSpeakers : undefined
    })
    onStart(controller)
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-10 md:px-6">
      <p className="text-body-sm text-muted-foreground">New session</p>
      <h1 className="mt-1 font-serif text-display-md text-text">Set up the room</h1>

      {!ready && (
        <div className="mt-6 rounded-lg border border-warn/40 bg-warn/10 p-4">
          <p className="text-body-sm text-text">
            {!hasKey
              ? 'No transcription key yet.'
              : 'Please review what leaves this machine before your first session.'}
          </p>
          <a href="#/settings">
            <Button size="sm" variant="outline" className="mt-3">
              <SettingsIcon /> Open settings
            </Button>
          </a>
        </div>
      )}

      <Labeled label="Session title">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Labeled>

      <Labeled label="Microphone">
        {devices.length === 0 ? (
          <Button variant="outline" onClick={enableMic}>
            <Mic /> Enable microphone
          </Button>
        ) : (
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-body-sm text-text"
          >
            {devices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Microphone ${i + 1}`}
              </option>
            ))}
          </select>
        )}
        {micError && (
          <p className="mt-1 text-label text-danger">{micError}</p>
        )}
      </Labeled>

      <div className="mt-6">
        <span className="mb-1 block text-label text-muted-foreground">
          Remote audio (optional)
        </span>
        <div className="flex items-start gap-3 rounded-md border border-border bg-background px-3 py-2.5">
          <div className="mt-0.5">
            <Switch
              checked={captureRemote}
              onCheckedChange={setCaptureRemote}
            />
          </div>
          <div>
            <p className="text-body-sm text-text">Capture a browser tab</p>
            <p className="text-label text-muted-foreground">
              Pull a Google Meet / Zoom tab’s audio in digitally so remote
              participants are transcribed too. You’ll choose the tab (and keep
              “Share tab audio” checked) when the session starts.
            </p>
          </div>
        </div>
      </div>

      <Labeled label="Expected speakers (optional)">
        <Input
          type="number"
          min={1}
          max={20}
          value={expected}
          placeholder="auto"
          onChange={(e) => setExpected(e.target.value)}
          className="w-28"
        />
      </Labeled>

      <div className="mt-8 flex items-center gap-3">
        <Button size="lg" disabled={!ready} onClick={start}>
          <Play /> Start session
        </Button>
        <span className="text-label text-muted-foreground">
          {PROVIDER_LABELS[keys.provider]}
          {keys.provider === 'speechmatics' &&
            ` · ${keys.speechmaticsRegion.toUpperCase()}`}
        </span>
      </div>
    </div>
  )
}

// --- live phase -------------------------------------------------------------

function LiveRun({
  controller,
  boardEndpoint,
  boardToken,
  onNew
}: {
  controller: SessionController
  boardEndpoint: string
  boardToken: string | null
  onNew: () => void
}) {
  const { store } = controller
  // The whiteboard pane: collapsed by default (the chat reads as a focused column);
  // opening it splits the view side-by-side. Only offered when a bridge is paired.
  const [boardOpen, setBoardOpen] = useState(false)
  const boardAvailable = boardToken != null
  const finals = useFinals(store)
  const interim = useInterim(store)
  const speakers = useSpeakers(store)
  const connection = useConnection(store)
  const detail = useConnectionDetail(store)
  const error = useError(store)
  const level = useLevel(store)
  const title = useSessionTitle(store)
  const aiTurns = useAiTurns(store)
  const aiStatus = useAiStatus(store)
  const aiDetail = useAiDetail(store)
  const aiPresence = useAiPresence(store)
  const delivery = useDelivery(store)
  const contacts = useContacts()
  const contactsById = Object.fromEntries(contacts.map((c) => [c.id, c]))
  // Debug Panel: only meaningful when a bridge is paired (it streams the
  // gateway's reflex events). Persisted toggle from the footer's "Debug" button.
  const [debugEnabled, setDebugEnabled] = useState(isDebugPanelEnabled)
  const showDebug = controller.ai != null && debugEnabled

  const stopped = connection === 'closed' || connection === 'error'
  const paused = connection === 'paused'
  const idle = connection === 'idle'

  // Render the session's title, status and controls into the shell's single top
  // bar (no separate stacked header). Memoized so it only updates the bar when
  // the title/connection/controls actually change, not on every transcript tick.
  const topbar = useMemo(() => {
    const boardToggle = boardAvailable ? (
      <Button
        size="sm"
        variant={boardOpen ? 'default' : 'outline'}
        onClick={() => setBoardOpen((o) => !o)}
      >
        <PanelRight /> Whiteboard
      </Button>
    ) : null
    const stateActions = stopped ? (
      <Button size="sm" onClick={onNew}>
        <Play /> New session
      </Button>
    ) : idle ? (
      <>
        <Button size="sm" onClick={() => controller.resume()}>
          <Play /> Resume capture
        </Button>
        <Button size="sm" variant="outline" onClick={onNew}>
          New session
        </Button>
      </>
    ) : (
      <>
        {paused ? (
          <Button size="sm" variant="outline" onClick={() => controller.resume()}>
            <Play /> Resume
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => controller.pause()}>
            <Pause /> Pause
          </Button>
        )}
        <Button size="sm" variant="destructive" onClick={() => controller.stop()}>
          <Square /> Stop
        </Button>
      </>
    )
    return {
      crumbs: [{ label: title }],
      titleIsHeading: true,
      labels: (
        <div className="flex flex-wrap items-center gap-2">
          <ConnectionChip state={connection} detail={detail} />
          {controller.ai != null && <AiPresenceChips presence={aiPresence} />}
        </div>
      ),
      actions: (
        <>
          {boardToggle}
          {stateActions}
        </>
      )
    }
  }, [
    title,
    connection,
    detail,
    stopped,
    paused,
    idle,
    controller,
    onNew,
    boardAvailable,
    boardOpen,
    aiPresence
  ])
  useTopBar(topbar)

  // Scroll the feed to a contribution's anchor line from the focus modal. The
  // feed lives in TranscriptView, but scrollIntoView walks up to the scroll
  // parent, so a document-level lookup works without threading a ref through.
  function jumpToSegment(segmentId: string | null) {
    if (!segmentId) return
    const sel = window.CSS?.escape ? window.CSS.escape(segmentId) : segmentId
    const el = document.querySelector<HTMLElement>(`[data-segid="${sel}"]`)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    el.classList.add('ring-2', 'ring-accent')
    window.setTimeout(() => el.classList.remove('ring-2', 'ring-accent'), 1200)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {controller.ai != null && (
        <FocusModal
          turns={aiTurns}
          speech={controller.speech}
          onJump={jumpToSegment}
        />
      )}
      {error && (
        <div className="flex items-center gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-2 md:px-6">
          <span className="text-body-sm text-destructive">{error.message}</span>
          {error.kind === 'auth' && (
            <a href="#/settings" className="text-body-sm underline">
              Fix in settings
            </a>
          )}
        </div>
      )}

      {/* main: chat reading column + optional whiteboard pane / speakers rail.
          Side-by-side on md+ (chat fixed-width left, board flexes right); below md
          it's a single column the Whiteboard toggle swaps (PRD 010 Phase C). */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Chat: a focused reading column on its own; a fixed-width column when the
            board is open (then hidden on mobile — the board takes the screen). The
            column owns its own padding so the whiteboard pane can claim the full
            width of the container with none. */}
        <div
          className={cn(
            'flex min-h-0 flex-col p-6',
            boardOpen
              ? 'hidden md:flex md:w-[44%] md:min-w-[360px] md:max-w-[760px] md:shrink-0 md:border-r md:border-border'
              : 'min-w-0 flex-1'
          )}
        >
          <div
            className={cn(
              'flex min-h-0 w-full flex-1 flex-col gap-3',
              !boardOpen && 'mx-auto max-w-[800px]'
            )}
          >
            <div className="min-h-0 flex-1">
              <TranscriptView
                finals={finals}
                interim={interim}
                speakers={speakers}
                aiTurns={aiTurns}
                aiStatus={aiStatus}
                aiDetail={aiDetail}
                aiPaired={controller.ai != null}
                contactsById={contactsById}
                delivery={delivery}
                onToggleDone={(id) => store.toggleAiTurnDone(id)}
                onSpeak={
                  controller.speech
                    ? (id) => controller.speech?.speak(id)
                    : undefined
                }
                debugEnabled={debugEnabled}
                onToggleDebug={
                  controller.ai != null
                    ? () => {
                      const next = !debugEnabled
                      setDebugPanelEnabled(next)
                      setDebugEnabled(next)
                    }
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        {/* Whiteboard pane — the SAME shared canvas as #/board, lazy-loaded so
            Excalidraw stays out of the session entry chunk; mounted only when open. */}
        {boardOpen && boardToken && (
          <div className="flex min-h-0 min-w-0 flex-1">
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center text-body-sm text-muted-foreground">
                  Loading whiteboard…
                </div>
              }
            >
              <BoardCanvas endpoint={boardEndpoint} token={boardToken} />
            </Suspense>
          </div>
        )}

        {/* Speakers / level rail — shown only when the board is closed. */}
        {!boardOpen && (
          <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto p-6 md:w-72 md:shrink-0">
            <RailSection label="Input level">
              <LevelMeter level={level} />
            </RailSection>
            <RailSection label="Speakers">
              <SpeakerList
                speakers={speakers}
                contacts={contacts}
                onRename={(id, name) => store.renameSpeaker(id, name)}
                onMerge={(keep, remove) => controller.mergeSpeakers(keep, remove)}
                onLink={(id, contactId) => store.linkSpeakerContact(id, contactId)}
              />
            </RailSection>
          </aside>
        )}
      </div>

      {/* In-flow at the bottom of the column so expanding it pushes the chat up
          (rather than overlapping it as a fixed bar would). */}
      {showDebug && (
        <DebugPanel
          controller={controller}
          onDisable={() => {
            setDebugPanelEnabled(false)
            setDebugEnabled(false)
          }}
        />
      )}
    </div>
  )
}

// --- small helpers ----------------------------------------------------------

function Labeled({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label className="mt-6 block">
      <span className="mb-1 block text-label text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}

function RailSection({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h2 className="mb-2 text-label font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </h2>
      {children}
    </section>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-body-sm text-muted-foreground">{children}</p>
    </div>
  )
}
